import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Card, Checkbox, Chip, Stack, Tooltip, Typography } from '@mui/material';
import {
    Add,
    CurrencyRupee,
    Edit,
    Interests,
    Lock,
    LockOpen,
    PlaylistRemove,
    PersonAdd,
    Savings,
    TextSnippet,
    InsertLink,
} from '@mui/icons-material';
import IdToDate from '../../common/date/IdToDate';
import CreatePaymentLinkMenu from './CreatePaymentLinkMenu';
import { getCategoryLabel, getCreatedByName, getFrozenByName, getPlatformNames, initials, safeAmount } from './campaignFieldUtils';

const iconButtonStyle = {
    fontSize: { xs: '22px', sm: '24px' },
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '10px',
    color: '#fff',
};

const CampaignCard = React.memo(function CampaignCard({
    campaignDetails,
    handleLinkGenerate,
    isDetailPage = false,
    selectable = false,
    selected = false,
    onToggleSelect,
    onRemoveFromGroup,
    isSuperUser = false,
    onAddRevenue,
    onEditBudgetLimit,
    onFreezeToggle,
    onShare,
}) {
    const navigate = useNavigate();

    const { name, deliverables, totalPayment, paymentLimit, isOfficeExpense, netRevenue, isFrozen } =
        campaignDetails;
    const category = getCategoryLabel(campaignDetails);
    const platform = getPlatformNames(campaignDetails).join(', ');
    const createdBy = getCreatedByName(campaignDetails);
    const frozenByName = getFrozenByName(campaignDetails);
    const campaignId = campaignDetails?._id || campaignDetails?.id;

    const openDetailsPage = useCallback(() => {
        navigate(`/payments/campaigns/details/${campaignId}`, {
            state: {
                id: campaignId,
                name: name,
                category: category,
                deliverables: deliverables,
                platform: platform,
                totalPayment: totalPayment,
                paymentLimit: paymentLimit,
                createdby: createdBy,
                isOfficeExpense: isOfficeExpense,
            },
        });
    }, [
        campaignId,
        name,
        category,
        deliverables,
        platform,
        totalPayment,
        paymentLimit,
        createdBy,
        navigate,
        isOfficeExpense,
    ]);

    const onVendorClick = useCallback(() => {
        navigate('/payments/paymentlink', {
            state: {
                id: campaignId,
                name: name,
                category: category,
                deliverables: deliverables,
                platform: platform,
                totalPayment: totalPayment,
                paymentLimit: paymentLimit,
                createdby: createdBy,
                isOfficeExpense: isOfficeExpense,
            },
        });
    }, [
        campaignId,
        name,
        category,
        deliverables,
        platform,
        totalPayment,
        paymentLimit,
        createdBy,
        navigate,
        isOfficeExpense,
    ]);

    const handleCardClick = useCallback(() => {
        if (selectable) {
            onToggleSelect?.(campaignId);
            return;
        }
        if (!isDetailPage) openDetailsPage();
    }, [selectable, onToggleSelect, campaignId, isDetailPage, openDetailsPage]);

    // The card on the (read-only) Details page is neither selectable nor
    // clickable-through, so it shouldn't invite a click with pointer/hover
    // affordances it then does nothing with.
    const isInteractive = selectable || !isDetailPage;

    return (
        <Card
            sx={{
                position: 'relative',
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                height: '100%',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                overflow: 'hidden',
                boxShadow: selected
                    ? '0 0 0 2px #E34F4F, 0 4px 14px rgba(0,0,0,0.08)'
                    : '0 1px 3px rgba(15, 23, 42, 0.08)',
                border: '1px solid',
                borderColor: selected ? 'transparent' : isFrozen ? '#F59E0B' : 'rgba(15, 23, 42, 0.06)',
                backgroundColor: isFrozen ? 'rgba(245, 158, 11, 0.04)' : undefined,
                cursor: isInteractive ? 'pointer' : 'default',
                transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                '&:hover': isInteractive
                    ? {
                          boxShadow: selected
                              ? '0 0 0 2px #E34F4F, 0 4px 14px rgba(0,0,0,0.08)'
                              : '0 6px 18px rgba(15, 23, 42, 0.12)',
                          transform: 'translateY(-1px)',
                      }
                    : undefined,
            }}
            onClick={handleCardClick}
        >
            {selectable && (
                <Checkbox
                    checked={selected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleSelect?.(campaignId)}
                    sx={{ position: 'absolute', top: 6, left: 6, zIndex: 1 }}
                />
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                    pl: selectable ? 4 : 0,
                }}
            >
                {/* Name gets its own full-width row — a long, space-less name
                    sharing a row with the icon/date row (below) would otherwise
                    get crushed by that row's fixed-width content and forced to
                    wrap character-by-character (overflow-wrap: anywhere with
                    nowhere reasonable left to break). */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        minWidth: 0,
                        width: '100%',
                    }}
                >
                    <Box
                        sx={{
                            alignSelf: 'flex-start',
                            color: '#fff',
                            backgroundColor: '#E34F4F',
                            borderRadius: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                        }}
                    >
                        <TextSnippet fontSize="small" />
                    </Box>
                    <Box sx={{ marginLeft: 1.5, minWidth: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                            }}
                        >
                            {name}
                        </Typography>
                        {category && (
                            <Chip
                                label={category}
                                size="small"
                                sx={{
                                    mt: 0.5,
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    backgroundColor: 'rgba(227, 79, 79, 0.1)',
                                    color: '#E34F4F',
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                        <IdToDate id={campaignDetails?.id || campaignDetails?._id} />
                    </Typography>

                    {!selectable && (
                        <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Frozen = view-only: every editing affordance below is
                                hidden for everyone, not just non-super-users — only
                                "View Campaign Links" (a read, not an edit) and the
                                Freeze/Unfreeze toggle itself stay available. */}
                            {!isFrozen && (isOfficeExpense ? (
                                <Tooltip title="Create Link">
                                    <Add
                                        sx={{ ...iconButtonStyle, backgroundColor: '#0AC380' }}
                                        onClick={onVendorClick}
                                    />
                                </Tooltip>
                            ) : (
                                <CreatePaymentLinkMenu
                                    onCreatorClick={
                                        !isDetailPage
                                            ? () => handleLinkGenerate(campaignDetails)
                                            : handleLinkGenerate
                                    }
                                    onVendorClick={onVendorClick}
                                />
                            ))}

                            {!isDetailPage && (
                                <Tooltip title="View Campaign Links">
                                    <InsertLink
                                        sx={{ ...iconButtonStyle, backgroundColor: '#e34f4f' }}
                                        onClick={openDetailsPage}
                                    />
                                </Tooltip>
                            )}

                            {!isFrozen && onRemoveFromGroup && (
                                <Tooltip title="Remove from group">
                                    <PlaylistRemove
                                        sx={{ ...iconButtonStyle, backgroundColor: '#64748B' }}
                                        onClick={() => onRemoveFromGroup(campaignId)}
                                    />
                                </Tooltip>
                            )}

                            {!isFrozen && isSuperUser && (
                                <Tooltip title="Add Revenue">
                                    <Savings
                                        sx={{ ...iconButtonStyle, backgroundColor: '#7C3AED' }}
                                        onClick={() => onAddRevenue?.(campaignDetails)}
                                    />
                                </Tooltip>
                            )}

                            {!isFrozen && isSuperUser && (
                                <Tooltip title="Edit Campaign">
                                    <Edit
                                        sx={{ ...iconButtonStyle, backgroundColor: '#0EA5E9' }}
                                        onClick={() => onEditBudgetLimit?.(campaignDetails)}
                                    />
                                </Tooltip>
                            )}

                            {/* Not hidden while frozen, unlike the editing
                                affordances above: sharing changes who may
                                *view* the campaign rather than its contents,
                                and a super user still needs to be able to let
                                someone in to review a frozen campaign. */}
                            {isSuperUser && onShare && (
                                <Tooltip title="Share Campaign">
                                    <PersonAdd
                                        sx={{ ...iconButtonStyle, backgroundColor: '#0891B2' }}
                                        onClick={() => onShare(campaignDetails)}
                                    />
                                </Tooltip>
                            )}

                            {isSuperUser && (
                                <Tooltip title={isFrozen ? 'Unfreeze Campaign' : 'Freeze Campaign'}>
                                    {isFrozen ? (
                                        <LockOpen
                                            sx={{ ...iconButtonStyle, backgroundColor: '#16A34A' }}
                                            onClick={() => onFreezeToggle?.(campaignDetails)}
                                        />
                                    ) : (
                                        <Lock
                                            sx={{ ...iconButtonStyle, backgroundColor: '#F59E0B' }}
                                            onClick={() => onFreezeToggle?.(campaignDetails)}
                                        />
                                    )}
                                </Tooltip>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            {isFrozen && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mt: 1,
                        px: 1.25,
                        py: 0.75,
                        borderRadius: 2,
                        backgroundColor: 'rgba(245, 158, 11, 0.12)',
                        color: '#92400E',
                    }}
                >
                    <Lock sx={{ fontSize: 16, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Frozen by {frozenByName || 'a super user'} — contact them if you want to
                        make edits, so they can unfreeze it.
                    </Typography>
                </Box>
            )}

            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    py: 1.5,
                    pl: selectable ? 4 : 0,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                }}
            >
                {deliverables}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: 1.5,
                    width: '100%',
                    pl: selectable ? 4 : 0,
                }}
            >
                <Stack direction="row" spacing={0.75} alignItems="center" minWidth={0}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: 11, bgcolor: '#94a3b8' }}>
                        {initials(createdBy)}
                    </Avatar>
                    <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                        {createdBy}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={2.5} flexWrap="wrap" rowGap={1}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Budget
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                            <CurrencyRupee sx={{ fontSize: '14px' }} />
                            {safeAmount(totalPayment)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Limit
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            color="error"
                            sx={{ display: 'flex', alignItems: 'center', fontWeight: 700, whiteSpace: 'nowrap' }}
                        >
                            <CurrencyRupee sx={{ fontSize: '14px' }} />
                            {safeAmount(paymentLimit)}
                        </Typography>
                    </Box>
                    {isSuperUser && netRevenue != null && (
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Net Revenue
                            </Typography>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontWeight: 700,
                                    whiteSpace: 'nowrap',
                                    color: netRevenue >= 0 ? '#0AC380' : '#e34f4f',
                                }}
                            >
                                <CurrencyRupee sx={{ fontSize: '14px' }} />
                                {netRevenue.toLocaleString('en-IN')}
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ maxWidth: 160, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            Platform
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontWeight: 700,
                                minWidth: 0,
                            }}
                        >
                            <Interests sx={{ fontSize: '14px', flexShrink: 0 }} color="action" />
                            {/* text-overflow only takes effect on the element whose
                                own content overflows it — putting it on the flex
                                container above (alongside the icon) is a no-op, so
                                the truncation has to live on this dedicated span,
                                with minWidth: 0 since flex items don't shrink below
                                their content size by default. */}
                            <Box
                                component="span"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                }}
                            >
                                {platform || '-'}
                            </Box>
                        </Typography>
                    </Box>
                </Stack>
            </Box>
        </Card>
    );
});

export default CampaignCard;
